import * as React from 'react';
import { BaseForm } from './Base';
import { Button, ButtonStyle } from '../Button';
import { ProfileManager, ProfileContext } from '../../profile';
import { useRequestAsync, getHeaders } from '../../api/client';
import Loader from '../Loader';
import { ErrorFlash } from '../Flash';
import theme from '../../utils/theme';

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;

// MAX_FILE_SIZE defines maximum of 1.%MB allowed in file size.
const MAX_FILE_SIZE = 1572864;

export interface Props extends InputProps {
  id: string;
  label: string;
  tooltip?: string;
  field: any;
  onValueChange: Function;
  defaultValue: any;
}

export interface ImageState {
  fileURL?: string;
  error?: Error;
}

const uploadFile = (context: ProfileManager) => {
  let pid = context.profile.id;
  if (context.profile.company_id) {
    pid = context.profile.company_id;
  }

  return useRequestAsync(
    (body: File) => ({
      body,
      url: `/upload/${pid}_${body.name}`,
      method: 'POST',
      headers: {
        'Content-Type': body.type,
        ...getHeaders(context),
      },
    }),
    false,
  );
};

export default (props: Props) => {
  const context = React.useContext(ProfileContext);
  const [file, setFile] = React.useState<File>(null);
  const [fileError, setFileError] = React.useState<Error>(null);
  const [state, submit] = uploadFile(context);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files.length > 0) {
      if (event.target.files[0].size > MAX_FILE_SIZE) {
        setFileError(
          new Error(
            'File must not be larger than 1.5MB. We recommend images with 1500 pixels in width',
          ),
        );
        return;
      }

      setFile(event.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (file) {
      submit(file);
    }
  };

  React.useEffect(() => {
    if (state.complete && state.data) {
      props.onValueChange(props.field, state.data.file_url);
    }
  }, [state]);

  if (state.loading) {
    return (
      <BaseForm label={props.label} id={props.id} tooltip={props.tooltip}>
        <Loader />
      </BaseForm>
    );
  }

  return (
    <BaseForm label={props.label} id={props.id} tooltip={props.tooltip}>
      <div
        className={`${theme.form.field.input} border-box w-100 flex items-center`}
      >
        <input
          className={'border-box bw0 b--transparent w-100 mr2'}
          id={props.id}
          type="file"
          onChange={handleFileChange}
          accept=".jpg, .jpeg, .png"
        />
        {file && (
          <Button className={ButtonStyle.Normal} onClick={handleUpload}>
            UPLOAD
          </Button>
        )}
      </div>
      {fileError && <ErrorFlash error={fileError} />}
      {state.error && <ErrorFlash error={state.error} />}
      {state.data && (
        <a className="link gold" href={state.data.file_url} target="_blank">
          VIEW
        </a>
      )}
    </BaseForm>
  );
};
